<template>
    <LandingPageLayout
        v-bind="landingPageSharedData.getLandingPageLayoutProps()"
    >
        <FeaturePageHeroSection
            title="Flexible meeting locations for every client experience"
            subtitle="Offer virtual, phone, and in-person session options while TinyCal handles link creation where supported."
            buttonLabel="Set meeting locations"
            :buttonUrl="heroRegisterUrl"
        />

        <section class="border-b border-slate-200 bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div class="grid gap-10 lg:grid-cols-2 lg:items-start">
                    <div>
                        <FeaturePageSectionHeader
                            title="Support virtual and physical sessions in one system"
                            subtitle="Choose location types based on the kind of experience each offer should create."
                            subtitleClass="mt-3 text-slate-600"
                        />

                        <FeaturePagePrimaryButton
                            class="mt-7"
                            label="Enable location options"
                            :href="ctaRegisterUrl"
                        />
                    </div>

                    <div>
                        <FeaturePageMockPanel
                            title="Meeting Location Policy"
                            badgeText="Flexible"
                        >
                            <FeaturePageInfoCard
                                v-for="locationPolicy in locationPolicies"
                                :key="locationPolicy.title"
                                :title="locationPolicy.title"
                                :description="locationPolicy.description"
                                containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />
                        </FeaturePageMockPanel>
                    </div>
                </div>
            </div>
        </section>

        <section class="border-b border-slate-200 bg-white">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Location options"
                    subtitle="Match each service format with the right meeting location and capture missing details at booking time."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <article
                        v-for="meetingLocationOption in meetingLocationOptions"
                        :key="meetingLocationOption.title"
                        class="rounded-xl border border-slate-200 bg-slate-50 p-4"
                    >
                        <div
                            class="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100"
                        >
                            <Image
                                v-if="
                                    meetingLocationOption.providerIconPath !==
                                    undefined
                                "
                                :src="meetingLocationOption.providerIconPath"
                                :alt="`${meetingLocationOption.title} logo`"
                                class="h-5 w-5 object-contain"
                            />
                            <MeetingLocationIcon
                                v-else
                                :type="
                                    meetingLocationOption.meetingLocationType
                                "
                                :iconSize="22"
                                class="text-slate-700"
                            />
                        </div>

                        <h3 class="text-base font-bold text-slate-900">
                            {{ meetingLocationOption.title }}
                        </h3>
                        <p class="mt-1 text-sm text-slate-600">
                            {{ meetingLocationOption.description }}
                        </p>
                    </article>
                </div>
            </div>
        </section>

        <section class="bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Sample use cases"
                    subtitle="Location flexibility helps each offer match the exact experience you want clients to have."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="sampleUseCase in sampleUseCases"
                        :key="sampleUseCase.title"
                        :title="sampleUseCase.title"
                        :description="sampleUseCase.description"
                        containerClass="rounded-xl border border-slate-200 bg-white p-5"
                        titleClass="text-lg font-bold"
                        descriptionClass="mt-2 text-sm text-slate-600"
                    />
                </div>
            </div>
        </section>
    </LandingPageLayout>
</template>

<script setup lang="ts">
    import { UtmContentEnum } from '@/Shared/Service/Tracking/Utm/UtmEnums'
    import { MeetingLocationTypeEnum } from '@/Shared/AutoGenerated/Main'
    import MeetingLocationIcon from '@/BookingForm/BookingForm/Page/Step/MeetingLocationIcon.vue'
    import FeaturePageHeroSection from '@/Marketing/LandingPage/Component/FeaturePage/HeroSection.vue'
    import FeaturePageInfoCard from '@/Marketing/LandingPage/Component/FeaturePage/InfoCard.vue'
    import FeaturePageMockPanel from '@/Marketing/LandingPage/Component/FeaturePage/MockPanel.vue'
    import FeaturePagePrimaryButton from '@/Marketing/LandingPage/Component/FeaturePage/PrimaryButton.vue'
    import FeaturePageSectionHeader from '@/Marketing/LandingPage/Component/FeaturePage/SectionHeader.vue'
    import LandingPageSharedData from '@/Marketing/LandingPage/Entity/LandingPageSharedData'
    import LandingPageLayout from '@/Marketing/LandingPage/Shared/LandingPageLayout.vue'
    import Image from '@/Shared/Component/Image.vue'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface LandingPageProps {
        landingPageSharedDataRawData: any
    }

    interface MeetingLocationOption {
        title: string
        description: string
        providerIconPath?: string
        meetingLocationType: MeetingLocationTypeEnum
    }

    interface LocationPolicy {
        title: string
        description: string
    }

    interface SampleUseCase {
        title: string
        description: string
    }

    const props = defineProps<LandingPageProps>()

    const landingPageSharedData = LandingPageSharedData.hydrate(
        props.landingPageSharedDataRawData,
    )
    const urlFactory = new UrlFactory()
    const heroRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.FlexibleMeetingLocationsHero,
    )
    const ctaRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.FlexibleMeetingLocationsCta,
    )

    const meetingLocationOptions: MeetingLocationOption[] = [
        {
            title: 'Google Meet',
            description:
                'TinyCal automatically sets up Google Meet links for bookings that use this location type.',
            providerIconPath: '/static/icons/providers/google-meet.svg',
            meetingLocationType: MeetingLocationTypeEnum.GoogleMeet,
        },
        {
            title: 'Zoom',
            description:
                'TinyCal automatically creates Zoom links so attendees receive join-ready details.',
            providerIconPath: '/static/icons/providers/zoom.svg',
            meetingLocationType: MeetingLocationTypeEnum.Zoom,
        },
        {
            title: 'Phone (attendee defined)',
            description:
                'Attendee provides the phone number during booking for callback-style meetings.',
            meetingLocationType: MeetingLocationTypeEnum.Phone,
        },
        {
            title: 'In person (attendee defined)',
            description:
                'Attendee specifies the location when the meeting venue is attendee-controlled.',
            meetingLocationType:
                MeetingLocationTypeEnum.InPersonAttendeeDefined,
        },
        {
            title: 'In person (host defined)',
            description:
                'Host defines the venue in advance for fixed-location meetings.',
            meetingLocationType: MeetingLocationTypeEnum.InPersonHostDefined,
        },
    ]

    const locationPolicies: LocationPolicy[] = [
        {
            title: 'Auto link generation',
            description:
                'Google Meet and Zoom links are created automatically when meetings are confirmed.',
        },
        {
            title: 'Attendee-supplied details',
            description:
                'Phone and attendee-defined in-person options collect location details at booking time.',
        },
        {
            title: 'Host-controlled venues',
            description:
                'Use host-defined in-person locations for offices, studios, or fixed event spaces.',
        },
    ]

    const sampleUseCases: SampleUseCase[] = [
        {
            title: 'Hybrid consulting',
            description:
                'Offer Google Meet for remote clients and in-person host-defined meetings for local sessions.',
        },
        {
            title: 'Local coaching sessions',
            description:
                'Use host-defined or attendee-defined locations when certain sessions happen in person.',
        },
        {
            title: 'Phone-first advisory calls',
            description:
                'Collect attendee phone details during booking for callback-heavy advisory or support workflows.',
        },
    ]
</script>
